import Marionette from "backbone.marionette";
import Radio from "backbone.radio";
import {
  isAuthenticated,
  login,
  getTokenValuesForHeaders,
} from "../utils/auth-management/sentinel";
import store from "../utils/webstorage";
import { CONSTANTS } from "../config/webstorage";
import User from "../models/user";
import routes from "./routes";

const channel = Radio.channel("global");

const DEFAULT_ROUTE = CONSTANTS.DEFAULT_ROUTE;

class Authentication extends Marionette.Object {
  initialize() {
    // setup channel listeners
    channel.reply("auth:credentials", this.getCredentials, this);
    channel.reply("auth:user", this.getUser, this);
    channel.reply("auth:user-shards", this.getUserShards, this);
    channel.reply("auth:user-default-shard", this.getUserDefaultShard, this);
    channel.reply("auth:user-id", this.getUserId, this);
    channel.reply("auth:user-email", this.getUserEmail, this);
    channel.reply("auth:user-photo", this.getUserPhoto, this);
    channel.reply("auth:default-route", this.getDefaultRoute, this);

    this.listenTo(channel, "auth:app-home", this.redirectHome);
    this.listenTo(channel, "auth:signout", this.signOut);
    this.listenTo(channel, "auth:expire", this.expire);
    this.listenTo(channel, "access-token-set", this.requestSessionToken);
    this.listenTo(channel, "session-token-set", this.loadUserProfile);

    // localize JWT
    this.token = (isAuthenticated() && getTokenValuesForHeaders()) || null;
    this.sessionToken = null;

    this.start();
  }

  start() {
    // make sure user has authenticated
    if (_.isEmpty(this.token)) {
      login();
    } else {
      // make user has session token
      if (_.isEmpty(this.sessionToken)) {
        // add access token to XHR headers. should trigger a session token request
        this.setAccessTokenHeader(this.token.accessToken);
      }
    }
  }

  requestSessionToken() {
    $.ajax({
      method: "post",
      url: `${routes.api}/auth`,
      data: { id_token: this.token.idToken },
      success: (data, textStatus, jqXHR) => {
        switch (jqXHR.status) {
          case 200:
            this.sessionToken = data;
            this.setSessionTokenHeader(this.sessionToken);

            break;
        }
      },
      error: () => {
        // redirect user to "access denied" page
        this.accessDenied();
      },
    });
  }

  loadUserProfile() {
    this.user = new User();
    this.user.promise.done(this.onUserLoaded.bind(this));
    this.user.promise.fail(this.onUserLoadFailure.bind(this));
  }

  onUserLoaded() {
    this.setDefaultRoute();

    // notify interested parties
    channel.trigger("auth-user-set", this.user.toJSON());
  }

  onUserLoadFailure() {
    // show error
    channel.trigger("status:show", {
      autoDismiss: false,
      type: "danger",
      message:
        "We were unable to retrieve your user profile. Please notify your administrator.",
    });
  }

  /**
   * Updates user profile with default route when found in localStorage
   */
  setDefaultRoute() {
    const route = store.getValue(DEFAULT_ROUTE);
    if (route) {
      this.user.setDefaultRoute(route);
    }
  }

  setAccessTokenHeader(token) {
    // add token header to jQuery configuration
    jQuery.ajaxSetup({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // notify interested parties
    channel.trigger("access-token-set");
  }

  setSessionTokenHeader(token) {
    // add token header to jQuery configuration
    jQuery.ajaxSetup({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // intercept ajax errors globally
    jQuery.ajaxSetup({
      statusCode: {
        401: this.expire.bind(this),
      },
    });

    // notify interested parties
    channel.trigger("session-token-set");
  }

  getCredentials() {
    const shard = this.getUserDefaultShard();
    return {
      token: this.sessionToken,
      shardId: shard ? shard.get("id") : null,
    };
  }

  /**
   * Returns a User property or the entire User model.
   *
   * @param {String} property
   */
  getUser(property = null) {
    return property ? this.user.get(property) : this.user;
  }

  getUserShards() {
    return this.getUser("shards");
  }

  getUserDefaultShard() {
    return this.getUser("defaultShard");
  }

  getUserId() {
    return this.getUser("id");
  }

  getUserEmail() {
    return this.getUser("email");
  }

  getUserPhoto() {
    return this.getUser("photoURL");
  }

  getDefaultRoute() {
    return this.getUser("defaultRoute");
  }

  redirectHome() {
    document.location.assign(routes.app);
  }

  signOut(timeout = false) {
    let endpoint = routes.signout;
    if (timeout) {
      endpoint += "?timeout=true";
    }

    // call to signout URL
    document.location.assign(endpoint);
  }

  expire() {
    // call to expire URL
    document.location.assign(routes.expire);
  }

  accessDenied() {
    // call to access-denied URL
    document.location.assign(routes.accessDenied);
  }
}

export { Authentication as default };
